import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/pro-light-svg-icons/faArrowsRotate';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { TypedMemo } from '../../../common/types';
import { platformMeta } from '../platformMeta';
import { SocialPlatform } from '../types';
import { ChannelNumbersState } from './channelNumbersState';
import { channelNumbersMessages as messages } from './channelNumbersMessages';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import shared from '../Promote.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ChannelStateLineProps {
  platform: SocialPlatform;
  state: ChannelNumbersState;
  /** Shows the line with no detail text below it (the Performance totals card). */
  compact?: boolean;
  disabled?: boolean;
  /** Starts the connect flow that exists. The two Reconnect lines call it. */
  onReconnect: (platform: SocialPlatform) => void;
}

/**
 * The one state line of a channel: "Reading your numbers", "Reconnect to see your numbers" or
 * "Reconnect". A channel with numbers and a manual channel have no line.
 */
const ChannelStateLineComponent: React.FC<ChannelStateLineProps> = ({
  platform,
  state,
  compact = false,
  disabled = false,
  onReconnect,
}) => {
  const intl = useIntl();
  const reconnect = useCallback(() => onReconnect(platform), [onReconnect, platform]);

  if (state === 'numbers' || state === 'none') {
    return null;
  }

  if (state === 'reading') {
    return (
      <>
        <div className={clsx(shared.stateLine, shared.stateLineInfo, compact && shared.stateLineCompact)}>
          <span className={shared.pulseDot} />
          <span className={shared.stateLineText}>{intl.formatMessage(messages.reading)}</span>
        </div>
        {compact ? null : <p className={shared.stateLineDetail}>{intl.formatMessage(messages.readingDetail)}</p>}
      </>
    );
  }

  const isWarn = state === 'reconnect';
  return (
    <>
      <button
        type="button"
        className={clsx(
          shared.stateLine,
          shared.stateLineButton,
          isWarn ? shared.stateLineWarn : shared.stateLineInfo,
          compact && shared.stateLineCompact
        )}
        disabled={disabled}
        onClick={reconnect}
      >
        <FontAwesomeIcon icon={faArrowsRotate} className={shared.stateLineIcon} />
        <span className={shared.stateLineText}>
          {intl.formatMessage(isWarn ? messages.reconnect : messages.reconnectForNumbers)}
        </span>
        <FontAwesomeIcon icon={faChevronRight} className={shared.stateLineIcon} />
      </button>
      {compact ? null : (
        <p className={shared.stateLineDetail}>
          {isWarn
            ? intl.formatMessage(messages.reconnectDetail, { platform: platformMeta[platform].name })
            : withBrandWord(intl.formatMessage(messages.reconnectForNumbersDetail))}
        </p>
      )}
    </>
  );
};

export const ChannelStateLine = TypedMemo(ChannelStateLineComponent);
